import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { X, Calendar, Clock, MessageCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const RichiestaColloquioModal = ({ isOpen, onClose, candidatoId, agenziaId, agenziaNome }) => {
  const [formData, setFormData] = useState({
    data_colloquio: "",
    ora_colloquio: "",
    messaggio: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.data_colloquio || !formData.ora_colloquio) {
      toast.error("Seleziona data e ora per il colloquio");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const requestData = {
        candidato_id: parseInt(candidatoId),
        agenzia_id: parseInt(agenziaId),
        data_colloquio: formData.data_colloquio,
        ora_colloquio: formData.ora_colloquio,
        messaggio: formData.messaggio || null
      };

      await axios.post(`${API}/api/v1/richieste-colloquio`, requestData);
      
      toast.success("Richiesta colloquio inviata con successo!");
      
      // Reset form
      setFormData({
        data_colloquio: "",
        ora_colloquio: "",
        messaggio: ""
      });
      
      onClose();
    } catch (error) {
      console.error("Error creating richiesta colloquio:", error);
      // Mostra il messaggio di errore dal backend se disponibile
      const errorMessage = error.response?.data?.detail || "Errore nell'invio della richiesta";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" style={{overflow: 'hidden'}}>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100 flex flex-col" style={{minHeight: '400px'}}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6 p-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Calendar className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Richiedi Colloquio
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="rounded-full p-1 h-8 w-8 hover:bg-gray-100"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Agenzia Info */}
        <div className="bg-blue-50 rounded-2xl p-4 mb-6 mx-6">
          <p className="text-sm text-gray-600 mb-1">Richiesta per</p>
          <p className="font-semibold text-gray-900">{agenziaNome}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 px-6 pb-6">
          {/* Data Colloquio */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4" />
              Data colloquio *
            </label>
            <input
              type="date"
              name="data_colloquio"
              value={formData.data_colloquio}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-colors"
            />
          </div>

          {/* Ora Colloquio */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4" />
              Ora colloquio *
            </label>
            <input
              type="time"
              name="ora_colloquio"
              value={formData.ora_colloquio}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-colors"
            />
          </div>

          {/* Messaggio Opzionale */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <MessageCircle className="w-4 h-4" />
              Scrivi un breve messaggio per l'agenzia
            </label>
            <textarea
              name="messaggio"
              value={formData.messaggio}
              onChange={handleChange}
              placeholder="Es: Sono molto interessato a lavorare con voi..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-colors resize-none"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-xl border-gray-300 hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-medium"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Invio..." : "Invia Richiesta"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RichiestaColloquioModal;